import { useMemo, useState, type FormEvent } from "react";
import type { Node, Space, Template } from "@melo/shared";
import { store, useTables } from "../lib/store";
import { descendantIds, formatDate, isDone, keyOf, todayIso } from "../lib/select";
import { send } from "../lib/actions";
import { toast } from "../lib/toast";
import { lower, milestoneWord, plural } from "../lib/words";
import { Dialog } from "./primitives";
import { DateInput } from "./pickers";
import { milestonesOf } from "./FilterBar";

type Cadence = "week" | "2weeks" | "month";
const CADENCES: { key: Cadence; label: string }[] = [
  { key: "week", label: "Every week" },
  { key: "2weeks", label: "Every 2 weeks" },
  { key: "month", label: "Every month" },
];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const day = (s: string) => new Date(`${s}T00:00:00`);
const addDays = (s: string, n: number) => {
  const d = day(s);
  d.setDate(d.getDate() + n);
  return iso(d);
};

/** The first `weekday` on or after `from`. */
function onOrAfter(from: string, weekday: number): string {
  const d = day(from);
  d.setDate(d.getDate() + ((weekday - d.getDay() + 7) % 7));
  return iso(d);
}

/** The last `weekday` of the month that `s` falls in. */
function lastOfMonth(s: string, weekday: number): string {
  const d = day(s);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  end.setDate(end.getDate() - ((end.getDay() - weekday + 7) % 7));
  return iso(end);
}

/** ISO 8601 week number, the one live teams say out loud ("week 40"). */
function isoWeek(s: string): number {
  const d = day(s);
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/** Ship dates, one per update: weekly and biweekly on a weekday, monthly on the month's last one. */
export function shipDates(first: string, cadence: Cadence, weekday: number, count: number): string[] {
  const out: string[] = [];
  // Starting today means the first one gets real time: a weekly update ships on the next ship day, a two-week sprint runs at least a week.
  let cur = cadence === "month" ? lastOfMonth(first, weekday) : onOrAfter(addDays(first, cadence === "2weeks" ? 8 : 1), weekday);
  if (cadence === "month" && cur < first) cur = lastOfMonth(addDays(lastOfMonth(first, weekday), 7), weekday);
  for (let i = 0; i < count; i++) {
    out.push(cur);
    if (cadence === "month") cur = lastOfMonth(addDays(cur, 10), weekday);
    else cur = addDays(cur, cadence === "week" ? 7 : 14);
  }
  return out;
}

const defaultsFor = (tpl: Template | undefined, word: string): { cadence: Cadence; pattern: string } => {
  const name = tpl?.name.toLowerCase() ?? "";
  if (name.includes("month")) return { cadence: "month", pattern: "{month} update" };
  if (name.includes("week")) return { cadence: "week", pattern: "Week {week} update" };
  if (name.includes("sprint")) return { cadence: "2weeks", pattern: "Sprint {n}" };
  if (name.includes("event")) return { cadence: "month", pattern: "{month} event" };
  return { cadence: "week", pattern: `${tpl?.name ?? word} {n}` };
};

function nameFor(pattern: string, ship: string, n: number) {
  return pattern
    .replaceAll("{n}", String(n))
    .replaceAll("{week}", String(isoWeek(ship)))
    .replaceAll("{month}", day(ship).toLocaleDateString("en-US", { month: "long" }))
    .replaceAll("{date}", formatDate(ship));
}

/** Matches names made from a pattern; the first group is {n} when the pattern has one. */
function patternRegex(pattern: string) {
  const parts = pattern.split(/(\{n\}|\{week\}|\{month\}|\{date\})/);
  const body = parts.map((p) => (p === "{n}" ? "(\\d+)" : /^\{(week|month|date)\}$/.test(p) ? ".+?" : p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))).join("");
  return new RegExp(`^${body}$`);
}

/** Where numbering picks up: one past the highest number already used by names made from this pattern. */
function nextNumber(pattern: string, titles: string[]) {
  if (!pattern.includes("{n}")) return 1;
  const re = patternRegex(pattern);
  let max = 0;
  for (const t of titles) {
    const m = re.exec(t);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return max + 1;
}

/** A series continues after its own last one: weekly updates follow the last weekly update, not a long monthly one. */
function seriesStart(pattern: string, milestones: Node[]) {
  const re = patternRegex(pattern);
  const last = milestones.filter((m) => re.test(m.title) && m.dueDate).map((m) => m.dueDate!).sort().at(-1);
  return last && last >= todayIso() ? addDays(last, 1) : todayIso();
}

/**
 * Schedules the next run of updates (or sprints) in one go: pick a template with its ship
 * checklist, a cadence and a ship day, and see every name and date before creating them.
 */
export function PlanUpdatesDialog({ space, onClose }: { space: Space; onClose: () => void }) {
  const t = useTables();
  const word = milestoneWord(t, space.id);
  const templates = [...t.templates.values()].filter((x) => x.spaceId === space.id && x.kindKey === "phase").sort((a, b) => a.name.localeCompare(b.name));
  const preferred = templates.find((x) => /week/i.test(x.name)) ?? templates.find((x) => /sprint/i.test(x.name)) ?? templates[0];
  const [templateId, setTemplateId] = useState(preferred?.id ?? "");
  const tpl = templateId ? t.templates.get(templateId) : undefined;
  const d = defaultsFor(tpl, word);
  const [cadence, setCadence] = useState<Cadence>(d.cadence);
  const [pattern, setPattern] = useState(d.pattern);
  const [weekday, setWeekday] = useState(5);
  const [count, setCount] = useState(4);
  const existing = milestonesOf(t, space.id);
  const [chosenFrom, setFrom] = useState<string | null>(null);
  const from = chosenFrom ?? seriesStart(pattern, existing);
  const [busy, setBusy] = useState(false);
  // Sprints usually run inside a milestone: work in a sprint then counts toward the milestone too.
  const containers = existing.filter((m) => !isDone(t, m) && !patternRegex(pattern).test(m.title));
  const sprintLike = (x: Template | undefined) => /sprint/i.test(x?.name ?? "");
  const [inside, setInside] = useState(() => (sprintLike(tpl) ? (containers.find((m) => !m.parentId)?.id ?? "") : ""));

  const pickTemplate = (id: string) => {
    setTemplateId(id);
    const next = t.templates.get(id);
    const nd = defaultsFor(next, word);
    setCadence(nd.cadence);
    setPattern(nd.pattern);
    setInside(sprintLike(next) ? (containers.find((m) => !m.parentId)?.id ?? "") : "");
  };
  const plan = useMemo(() => {
    const dates = shipDates(from, cadence, weekday, Math.max(1, Math.min(12, count)));
    const start = nextNumber(pattern, existing.map((m) => m.title));
    return dates.map((ship, i) => {
      const prev = i === 0 ? null : dates[i - 1]!;
      // The first one starts on the chosen day; each after it starts the day after the one before ships.
      const startDate = prev ? addDays(prev, 1) : from;
      return { title: nameFor(pattern, ship, start + i), startDate, dueDate: ship };
    });
  }, [from, cadence, weekday, count, pattern, existing]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await send("node.create", { spaceId: space.id, nodes: plan.map((p) => ({ kind: "phase", title: p.title, startDate: p.startDate, dueDate: p.dueDate, template: templateId || undefined, parentId: inside || undefined })) });
      toast(`Planned ${plan.length} ${lower(plan.length === 1 ? word : plural(word))}`);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={`Plan ${lower(plural(word))}`} onClose={onClose} wide>
      <form className="form" onSubmit={submit}>
        <div className="create-props">
          <label className="prop">
            <span className="label">Template</span>
            <select className="select" value={templateId} onChange={(e) => pickTemplate(e.target.value)} aria-label="Template">
              <option value="">None</option>
              {templates.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                  {x.checklist.length ? ` · ${x.checklist.length} steps` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="prop">
            <span className="label">How often</span>
            <select className="select" value={cadence} onChange={(e) => setCadence(e.target.value as Cadence)} aria-label="How often">
              {CADENCES.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="prop">
            <span className="label">{cadence === "month" ? "Ships on the last" : "Ships on"}</span>
            <select className="select" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} aria-label="Ship day">
              {WEEKDAYS.map((w, i) => (
                <option key={w} value={i}>
                  {w}
                  {cadence === "month" ? " of the month" : ""}
                </option>
              ))}
            </select>
          </label>
          <div className="prop">
            <span className="label">Starting</span>
            <DateInput className="field-btn" label="Starting" value={from} onSave={(v) => v && setFrom(v)} />
          </div>
          <label className="prop">
            <span className="label">How many</span>
            <input className="input" type="number" min={1} max={12} value={count} onChange={(e) => setCount(Number(e.target.value) || 1)} aria-label="How many" />
          </label>
          {containers.length > 0 && (
            <label className="prop">
              <span className="label">Inside</span>
              <select className="select" value={inside} onChange={(e) => setInside(e.target.value)} aria-label="Inside">
                <option value="">Nothing, on their own</option>
                {containers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="prop">
            <span className="label">Name</span>
            <input className="input mono" value={pattern} onChange={(e) => setPattern(e.target.value)} aria-label="Name pattern" />
          </label>
        </div>
        <p className="hint">
          Names can use <code>{"{week}"}</code>, <code>{"{month}"}</code>, <code>{"{date}"}</code> and <code>{"{n}"}</code>, which counts on from the last one.
          {tpl?.checklist.length ? ` Each gets the ${tpl.name} checklist: ${tpl.checklist.join(", ")}.` : ""}
        </p>
        <div className="sched" aria-label="Preview">
          {plan.map((p) => (
            <div key={p.dueDate} className="sched-row">
              <span className="sched-t">{p.title}</span>
              <span className="muted">
                {formatDate(p.startDate)} to {formatDate(p.dueDate)}
              </span>
            </div>
          ))}
        </div>
        <div className="form-actions">
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn pri" disabled={busy || !plan.length || !pattern.trim()}>
            Create {plan.length} {lower(plan.length === 1 ? word : plural(word))}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Work directly inside a milestone, split into finished and not. Subtasks travel with their parent. */
export function milestoneWork(t: ReturnType<typeof useTables>, m: Node) {
  const kids = [...t.nodes.values()].filter((n) => n.parentId === m.id && !n.draft);
  return { done: kids.filter((n) => isDone(t, n)), open: kids.filter((n) => !isDone(t, n)) };
}

/** Closes an update or milestone, and says plainly what happens to the work that did not make it. */
export function ShipDialog({ milestone, onClose }: { milestone: Node; onClose: () => void }) {
  const t = useTables();
  const word = milestoneWord(t, milestone.spaceId);
  const { open } = milestoneWork(t, milestone);
  // Everything inside counts toward done, the same way the overview counts it; only direct work moves (subtasks travel with it).
  const inner = descendantIds(t, milestone.id);
  const all = [...t.nodes.values()].filter((n) => inner.has(n.id) && !n.draft && t.kinds.get(n.kindId)?.key !== "phase");
  const allDone = all.filter((n) => isDone(t, n)).length;
  const others = milestonesOf(t, milestone.spaceId).filter((m) => m.id !== milestone.id && !isDone(t, m));
  // Unfinished work goes to the next one in the same series: the next sprint in this milestone, or the next update.
  const later = (m: Node) => (m.dueDate ?? "9999") >= (milestone.dueDate ?? "");
  const siblings = others.filter((m) => m.parentId === milestone.parentId && !descendantIds(t, milestone.id).has(m.id));
  const next = siblings.find(later) ?? others.find(later) ?? others[0];
  const [moveTo, setMoveTo] = useState(next?.id ?? "");
  const [busy, setBusy] = useState(false);
  const unchecked = milestone.checklist.filter((c) => !c.done);
  const ship = async () => {
    setBusy(true);
    try {
      const r = (await send("milestone.ship", { milestoneId: milestone.id, moveTo: moveTo || null })) as { carried: number };
      const where = moveTo ? store.t.nodes.get(moveTo)?.title : null;
      toast(r.carried ? `Shipped ${milestone.title}. ${r.carried} unfinished ${r.carried === 1 ? "item" : "items"} moved to ${where ?? `no ${lower(word)}`}.` : `Shipped ${milestone.title}.`);
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={`Ship ${milestone.title}`} onClose={onClose}>
      <div className="form">
        <p>
          {allDone} of {all.length} {all.length === 1 ? "item" : "items"} done.
        </p>
        {unchecked.length > 0 && (
          <div className="banner signal">
            <span className="grow">
              {unchecked.length} ship checklist {unchecked.length === 1 ? "step is" : "steps are"} not ticked: {unchecked.map((c) => c.text).join(", ")}.
            </span>
          </div>
        )}
        {open.length > 0 && (
          <>
            <label className="field-row">
              <span className="label">
                Move {open.length} unfinished {open.length === 1 ? "item, with its subtasks," : "items, with their subtasks,"} to
              </span>
              <select className="select" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} aria-label="Move unfinished work to">
                {others.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                    {m.dueDate ? `, ships ${formatDate(m.dueDate)}` : ""}
                  </option>
                ))}
                <option value="">No {lower(word)}</option>
              </select>
            </label>
            <div className="subs">
              {open.slice(0, 8).map((n) => (
                <div key={n.id} className="sub">
                  <span className="mono muted">{keyOf(t, n)}</span>
                  <span className="grow">{n.title}</span>
                </div>
              ))}
              {open.length > 8 && <div className="sub muted">and {open.length - 8} more</div>}
            </div>
          </>
        )}
        <div className="form-actions">
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn pri" disabled={busy} onClick={() => void ship()}>
            Ship {lower(word)}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
